import { useEffect, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import OlMap from 'ol/Map';
import OlView from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import XYZ from 'ol/source/XYZ';
import { transformExtent } from 'ol/proj';
import { getIntersection, isEmpty } from 'ol/extent';
import {
  buildArcGISStyle,
  fetchArcGISTileFeatures,
  getArcGISOverviewInfo,
} from '../map/utils';
import 'ol/ol.css';

// Lon/lat bounds of California: the fallback when a layer's own extent is
// missing or unusable, and a cap on it - some layers' recorded extents are
// far too big (Fire Hazard Severity Zones' metadata and CKAN extents both
// span most of North America), which would shrink the state to a speck.
const CALIFORNIA_EXTENT_4326 = [-124.5, 32.5, -114.1, 42.1];
const CALIFORNIA_EXTENT = transformExtent(
  CALIFORNIA_EXTENT_4326,
  'EPSG:4326',
  'EPSG:3857'
);

// The layer metadata's extent, in EPSG:3857, or null if we can't use it.
// The part of an EPSG:3857 extent inside California, or null if none.
const clampToCalifornia = (extent) => {
  const clamped = extent && getIntersection(extent, CALIFORNIA_EXTENT);
  return clamped && !isEmpty(clamped) ? clamped : null;
};

const metadataExtent = (extent) => {
  const wkid =
    extent?.spatialReference?.latestWkid ?? extent?.spatialReference?.wkid;
  if (!extent || !Number.isFinite(extent.xmin)) return null;
  const bounds = [extent.xmin, extent.ymin, extent.xmax, extent.ymax];
  if (wkid === 3857 || wkid === 102100) return bounds;
  try {
    return transformExtent(bounds, `EPSG:${wkid}`, 'EPSG:3857');
  } catch {
    return null; // a projection proj4 hasn't been told about
  }
};

// A static preview map for an ArcGIS-hosted dataset, in place of the
// GeoServer WMS thumbnail (which can't draw these). Feature layers are drawn
// from one simplified, binary tile query over the whole preview (see
// fetchArcGISTileFeatures) - for the biggest layers that's their largest
// few thousand features, plenty at this size. Cached tile layers are just
// drawn from their tiles. Calls onError if the layer can't be shown.
const ArcGISMiniMap = ({ gisService, title, spatialExtent, onError }) => {
  const targetRef = useRef(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const isTile = gisService.service_type === 'ArcGISTile';
    const features = new VectorSource();
    const map = new OlMap({
      target: targetRef.current,
      controls: [],
      interactions: [],
      layers: [
        new TileLayer({
          source: new XYZ({
            crossOrigin: 'anonymous',
            url: 'https://api.maptiler.com/maps/positron/{z}/{x}/{y}.png?key=8IPOL0tHId7YCUvo6p9h',
            maxZoom: 20,
          }),
        }),
        isTile
          ? new TileLayer({
              opacity: 0.8,
              source: new XYZ({
                url: gisService.tile_url,
                crossOrigin: 'anonymous',
                minZoom: gisService.min_zoom,
                maxZoom: gisService.max_zoom,
              }),
            })
          : new VectorLayer({
              source: features,
              style: buildArcGISStyle(gisService.color_map),
              opacity: 0.8,
            }),
      ],
      view: new OlView(),
    });

    const view = map.getView();
    const fit = (extent) =>
      view.fit(extent, { size: map.getSize(), padding: [8, 8, 8, 8] });
    const lonLatExtent = gisService.extent_4326 ?? spatialExtent;
    // Right away, so the basemap shows while the layer loads.
    fit(
      (lonLatExtent &&
        clampToCalifornia(
          transformExtent(lonLatExtent, 'EPSG:4326', 'EPSG:3857')
        )) ??
        CALIFORNIA_EXTENT
    );

    let cancelled = false;
    const load = async () => {
      const info = isTile
        ? null
        : await getArcGISOverviewInfo(
            gisService.service_url,
            gisService.color_map
          );
      if (cancelled || isTile) return;
      const layerExtent = clampToCalifornia(metadataExtent(info.extent));
      if (layerExtent) fit(layerExtent);
      features.addFeatures(
        await fetchArcGISTileFeatures({
          serviceUrl: gisService.service_url,
          info,
          extent: view.calculateExtent(map.getSize()),
          srid: '3857',
          resolution: view.getResolution(),
        })
      );
    };
    load()
      .catch((error) => {
        if (cancelled) return;
        console.error('Failed to draw ArcGIS map preview:', error);
        onError?.(error);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      map.setTarget(null);
    };
    // onError is a fresh closure each render; the preview only depends on
    // which layer it's drawing.
  }, [gisService, spatialExtent]);

  return (
    <div
      className="dataset-map dataset-minimap"
      role="img"
      aria-label={`Map preview of ${title || 'this dataset'}`}
    >
      <div ref={targetRef} className="dataset-minimap-target" />
      {loading && (
        <div className="dataset-minimap-loading">
          <Spinner animation="border" size="sm" variant="success" />
        </div>
      )}
    </div>
  );
};

export default ArcGISMiniMap;
